import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Message from '@decisiv/ui-components/lib/components/Message';
import { P, H4 } from '@decisiv/ui-components/lib/components/Typography';
import AsteriskIcon from '@decisiv/iconix/lib/components/Asterisk';

import { TextField, Select, PhoneTextField } from 'components/Form';
import { getStatesByCountry } from 'utils/countryRegions';
import { COUNTRY_OPTIONS, DIAL_CODE_OPTIONS } from 'pages/Management/constants';
import { useCurrentUserContext } from 'utils/User';
import fleetAdminDeniedReasonText from 'utils/User/fleetAdminDeniedReason';
import { codeToCountry } from 'utils/codeToCountry';
import { CountryCode } from 'libphonenumber-js';
import { useCurrentUserManagedTeamsQuery } from './useCurrentUserManagedTeamsQuery';
import { buildOemAndTeamOptions } from './utils/buildOemAndTeamOptions';

interface ContentProps {
  capability: FleetAdminCapability | null;
}

const Content = ({ capability }: ContentProps) => {
  const isDenied = capability?.allowed === false;
  const { watch, setValue } = useFormContext();
  const { isSuperUser } = useCurrentUserContext();

  const countryValue = watch('country');
  const oemSegmentValue = watch('oemSegmentId');
  const countryCodeValue = watch('countryCode');

  const {
    data: {
      currentUserProfile: { user: { managedSentinelTeams = [] } = {} } = {},
    } = {},
  } = useCurrentUserManagedTeamsQuery();

  const [oemSegmentOptions, teamsBySegmentMap] = useMemo(
    () => buildOemAndTeamOptions(managedSentinelTeams),
    [managedSentinelTeams],
  );

  const teamOptions = useMemo(() => {
    // Non Super Admins doesn't see OEM Segment dropdown. So since the API is already
    // Scoped for the ones the User has access, we simply show all teams.
    if (!isSuperUser) {
      return managedSentinelTeams.map((team) => ({
        id: team.id,
        label: team.name,
        value: team.id,
      }));
    }

    /* istanbul ignore next -- @preserve -- just narrowing down the type */
    if (!oemSegmentValue) return [];

    return (
      teamsBySegmentMap.get(oemSegmentValue) ||
      /* istanbul ignore next -- @preserve */ []
    );
  }, [oemSegmentValue, teamsBySegmentMap, managedSentinelTeams, isSuperUser]);

  const stateOptions = useMemo(
    () => getStatesByCountry(countryValue),
    [countryValue],
  );

  const handleOnCountryChange = useCallback(() => {
    setValue('state', '');
  }, [setValue]);

  const handleOnOemSegmentChange = useCallback(() => {
    setValue('sentinelTeamId', '');
  }, [setValue]);

  return (
    <Flex flex={1} flexDirection="column" marginTop={1}>
      <Flex justifyContent="flex-end" marginBottom={1}>
        <P size="small" shade={1}>
          <AsteriskIcon
            size="medium"
            color={toColorString(color.status.information.medium)}
          />
          {t`Required Fields`}
        </P>
      </Flex>

      {isSuperUser && (
        <Flex marginBottom={1}>
          <Select
            name="oemSegmentId"
            label={t`OEM Segment`}
            options={oemSegmentOptions}
            onChange={handleOnOemSegmentChange}
            required
            clearButtonLabel={t`Clear OEM Segment`}
            defaultValue=""
          />
        </Flex>
      )}

      {isDenied && capability && (
        <Flex marginBottom={1}>
          <Message intent="warning">
            {fleetAdminDeniedReasonText(capability.deniedReason)}
          </Message>
        </Flex>
      )}

      <Flex marginBottom={1}>
        <Select
          name="sentinelTeamId"
          label={t`Team`}
          options={teamOptions}
          required
          clearButtonLabel={t`Clear Team`}
          defaultValue=""
        />
      </Flex>

      <Flex marginBottom={1}>
        <TextField name="name" label={t`Name`} required disabled={isDenied} />
      </Flex>

      <Flex marginBottom={1}>
        <TextField
          name="address1"
          label={t`Address 1`}
          required
          disabled={isDenied}
        />
      </Flex>

      <Flex marginBottom={1}>
        <TextField name="address2" label={t`Address 2`} disabled={isDenied} />
      </Flex>

      <Flex marginBottom={1}>
        <Flex flex={1}>
          <TextField name="city" label={t`City`} required disabled={isDenied} />
        </Flex>

        <Flex flex={1} marginLeft={1}>
          <Select
            name="state"
            label={t`State`}
            options={stateOptions}
            required
            clearButtonLabel={t`Clear State`}
            defaultValue=""
            disabled={isDenied}
          />
        </Flex>
      </Flex>

      <Flex marginBottom={1}>
        <Flex flex={1}>
          <TextField
            name="postalCode"
            label={t`Postal Code`}
            required
            disabled={isDenied}
          />
        </Flex>

        <Flex flex={1} marginLeft={1}>
          <Select
            name="country"
            label={t`Country`}
            options={COUNTRY_OPTIONS}
            required
            defaultValue=""
            onChange={handleOnCountryChange}
            disabled={isDenied}
          />
        </Flex>
      </Flex>

      <Flex marginTop={2}>
        <H4>
          {t`Contact Information`}{' '}
          <AsteriskIcon
            size="medium"
            color={toColorString(color.status.information.medium)}
          />
        </H4>
      </Flex>

      <Flex marginBottom={1}>
        <P size="small" shade={1}>
          {t`Provide at least an email address or phone number.`}
        </P>
      </Flex>

      <Flex marginBottom={1}>
        <Flex flex={0} marginRight={1} style={{ minWidth: '120px' }}>
          <Select
            name="countryCode"
            label={t`Country Code`}
            options={DIAL_CODE_OPTIONS}
            disabled={isDenied}
          />
        </Flex>
        <Flex flex={1}>
          <PhoneTextField
            name="phoneNumber"
            label={t`Phone Number`}
            countryCode={codeToCountry(countryCodeValue) as CountryCode}
            disabled={isDenied}
          />
        </Flex>
      </Flex>

      <Flex>
        <TextField
          name="email"
          label={t`Email`}
          type="email"
          disabled={isDenied}
        />
      </Flex>
    </Flex>
  );
};

export default Content;
